import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { LayoutDashboard, Inbox as InboxIcon, ImageIcon, LogOut, KeyRound, Loader2, Globe, CalendarDays } from 'lucide-react';
import StatsPanel from './StatsPanel';
import InboxPanel from './InboxPanel';
import MediaPanel from './MediaPanel';
import AvailabilityPanel from './AvailabilityPanel';
import ChangePasswordDialog from './ChangePasswordDialog';

const TABS = [
  { id: 'stats', label: 'Statistics', icon: LayoutDashboard },
  { id: 'inbox', label: 'Inbox', icon: InboxIcon },
  { id: 'agenda', label: 'Agenda', icon: CalendarDays },
  { id: 'media', label: 'Media Library', icon: ImageIcon },
];

export default function AdminDashboard() {
  const { admin, loading, logout } = useAuth();
  const [tab, setTab] = useState('stats');
  const [pwOpen, setPwOpen] = useState(false);

  if (loading) {
    return <div className="min-h-screen bg-[#072A1F] flex items-center justify-center text-white/70"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#072A1F] text-white">
      {/* Top bar */}
      <header className="border-b border-[#D4AF37]/15 bg-[#083325]/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1280px] mx-auto px-6 h-16 flex items-center justify-between gap-3">
          <div className="font-serif-display text-xl">
            Gold Care <span className="peach-text italic">Admin</span>
          </div>
          <nav className="hidden md:flex items-center gap-1 bg-[#124D3A] border border-[#D4AF37]/15 rounded-full p-1.5">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id} onClick={() => setTab(id)}
                data-testid={`admin-tab-${id}`}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] transition-colors ${
                  tab === id ? 'bg-[#D4AF37] text-[#0B3D2E]' : 'text-white/70 hover:text-[#D4AF37]'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a
              href="/" target="_blank" rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 text-xs text-white/65 hover:text-[#D4AF37] px-3 py-1.5 rounded-full border border-[#D4AF37]/25"
            >
              <Globe className="w-3.5 h-3.5" /> View site
            </a>
            <button
              onClick={() => setPwOpen(true)}
              data-testid="admin-change-pw-btn"
              className="text-xs text-white/70 hover:text-[#D4AF37] px-3 py-1.5 rounded-full border border-[#D4AF37]/25 flex items-center gap-1.5"
            >
              <KeyRound className="w-3.5 h-3.5" /> Password
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-xs text-red-200 hover:text-white px-3 py-1.5 rounded-full bg-red-500/15 border border-red-400/30 flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
        {/* Mobile tab switcher */}
        <div className="md:hidden flex items-center gap-1 px-4 pb-3">
          {TABS.map(({ id, label }) => (
            <button
              key={id} onClick={() => setTab(id)}
              data-testid={`admin-tab-mobile-${id}`}
              className={`flex-1 py-2 rounded-full text-[11px] uppercase tracking-[0.15em] ${
                tab === id ? 'bg-[#D4AF37] text-[#0B3D2E]' : 'bg-[#124D3A] text-white/75'
              }`}
            >{label}</button>
          ))}
        </div>
      </header>

      <main className="max-w-[1280px] mx-auto px-6 py-8">
        <div className="mb-6 text-sm text-white/60">Signed in as <span className="text-[#D4AF37]">{admin.email}</span></div>
        {tab === 'stats' && <StatsPanel />}
        {tab === 'inbox' && <InboxPanel />}
        {tab === 'agenda' && <AvailabilityPanel />}
        {tab === 'media' && <MediaPanel />}
      </main>

      {pwOpen && <ChangePasswordDialog onClose={() => setPwOpen(false)} />}
    </div>
  );
}
